import * as THREE from 'three';

/** Neon billboard + towers — navigation anchors (prd2 § Landmarks). */
export function addLandmarks(root: THREE.Group, half: number): THREE.Object3D[] {
  const spin: THREE.Object3D[] = [];

  const signTex = lastMileSignTexture();
  const signMat = new THREE.MeshStandardMaterial({
    map: signTex,
    emissive: 0xff00aa,
    emissiveMap: signTex,
    emissiveIntensity: 1.2,
    roughness: 0.4,
    metalness: 0.2,
    transparent: true,
  });
  const sign = new THREE.Mesh(new THREE.PlaneGeometry(320, 72), signMat);
  sign.position.set(0, 110, half * 0.88);
  sign.rotation.y = Math.PI;
  sign.castShadow = true;
  root.add(sign);

  const pole = new THREE.Mesh(
    new THREE.BoxGeometry(24, 200, 24),
    new THREE.MeshStandardMaterial({
      color: 0x1a2230,
      metalness: 0.5,
      roughness: 0.45,
    }),
  );
  pole.position.set(0, 100, half * 0.88);
  pole.castShadow = true;
  root.add(pole);

  const glassMat = new THREE.MeshStandardMaterial({
    color: 0x22ff88,
    emissive: 0x114433,
    emissiveIntensity: 0.45,
    metalness: 0.75,
    roughness: 0.18,
    transparent: true,
    opacity: 0.92,
  });
  const tower = new THREE.Mesh(
    new THREE.BoxGeometry(55, 220, 55),
    glassMat,
  );
  tower.position.set(-180, 110, half * 0.22);
  tower.castShadow = true;
  root.add(tower);

  const radarGroup = new THREE.Group();
  radarGroup.position.set(half * 0.58, 95, -half * 0.48);
  const dish = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 38, 4, 24),
    new THREE.MeshStandardMaterial({
      color: 0x8899aa,
      metalness: 0.6,
      roughness: 0.35,
    }),
  );
  dish.rotation.z = Math.PI / 2;
  dish.position.y = 2;
  radarGroup.add(dish);
  const bowl = new THREE.Mesh(
    new THREE.SphereGeometry(36, 20, 12, 0, Math.PI * 2, 0, Math.PI * 0.42),
    new THREE.MeshStandardMaterial({
      color: 0x9aaab8,
      metalness: 0.45,
      side: THREE.DoubleSide,
    }),
  );
  bowl.rotation.x = Math.PI;
  bowl.position.y = 22;
  radarGroup.add(bowl);
  const mast = new THREE.Mesh(
    new THREE.CylinderGeometry(4, 6, 90, 8),
    new THREE.MeshStandardMaterial({ color: 0x606a78, roughness: 0.55 }),
  );
  mast.position.y = 45;
  radarGroup.add(mast);
  root.add(radarGroup);
  spin.push(bowl);

  const containerMat = new THREE.MeshStandardMaterial({
    color: 0xc45a28,
    metalness: 0.25,
    roughness: 0.65,
  });
  for (let i = 0; i < 5; i++) {
    const c = new THREE.Mesh(
      new THREE.BoxGeometry(52 + i * 4, 34, 22),
      containerMat,
    );
    c.position.set(-half * 0.62 + i * 28, 17, half * 0.58 + (i % 2) * 12);
    c.rotation.y = (i * 0.07) % 0.3;
    c.castShadow = true;
    root.add(c);
  }

  const waterTower = new THREE.Group();
  waterTower.position.set(-half * 0.42, 0, -half * 0.52);
  const legMat = new THREE.MeshStandardMaterial({
    color: 0x8a9098,
    metalness: 0.35,
    roughness: 0.5,
  });
  for (const [lx, lz] of [
    [-18, -18],
    [18, -18],
    [-18, 18],
    [18, 18],
  ] as [number, number][]) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(3, 4, 55, 6), legMat);
    leg.position.set(lx, 27, lz);
    waterTower.add(leg);
  }
  const tank = new THREE.Mesh(
    new THREE.SphereGeometry(42, 20, 14),
    new THREE.MeshStandardMaterial({
      color: 0xccd8e8,
      metalness: 0.4,
      roughness: 0.4,
    }),
  );
  tank.scale.set(1, 0.55, 1);
  tank.position.y = 68;
  waterTower.add(tank);
  root.add(waterTower);

  // Removed accent PointLights for performance — landmarks use emissive materials.

  return spin;
}

function lastMileSignTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 256;
  const g = c.getContext('2d')!;
  g.fillStyle = '#0a0812';
  g.fillRect(0, 0, 1024, 256);
  g.strokeStyle = 'rgba(255,0,180,0.9)';
  g.lineWidth = 8;
  g.strokeRect(16, 16, 992, 224);
  g.font = 'bold 112px Inter, system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = '#ff66cc';
  g.shadowColor = '#ff00aa';
  g.shadowBlur = 28;
  g.fillText('LAST MILE', 512, 128);
  g.shadowBlur = 0;
  g.fillStyle = 'rgba(255,255,255,0.85)';
  g.font = '600 28px Inter, system-ui, sans-serif';
  g.fillText('URBAN CHASE', 512, 198);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}
